<template>
  <SideModal
    :show="student && show"
    @update:show="
      {
        if (!state.loadingSave) $emit('update:show', $event);
      }
    "
    location="left"
    close-on-overlay-click
  >
    <div class="modal-container">
      <div class="modal-top">
        <span class="d-flex gap-3 align-center">
          <Typography color="primary" weight="600" size="1.25rem">Edit Student</Typography>
          <v-icon size="x-small" @click="copyToClipboard(student.uid)">mdi-content-copy</v-icon>
        </span>
        <v-btn
          @click="
            {
              if (!state.loadingSave) $emit('update:show', false);
            }
          "
          variant="plain"
          color="normal"
          icon
          ><v-icon color="gray600">mdi-close</v-icon></v-btn
        >
      </div>

      <form @submit.prevent="save" class="modal-body">
        <v-text-field
          :disabled="student.deleted"
          class="custom-input"
          color="primary"
          label="First Name"
          v-model="state.child_fname"
        ></v-text-field>
        <v-text-field
          :disabled="student.deleted"
          class="custom-input"
          color="primary"
          label="Last Name"
          v-model="state.child_lname"
        ></v-text-field>
        <v-text-field
          :disabled="student.deleted"
          class="custom-input"
          color="primary"
          label="Description"
          v-model="state.description"
        ></v-text-field>
        <v-text-field
          class="custom-input"
          color="primary"
          label="Student ID"
          disabled
          :model-value="props.student.external_child_id"
        ></v-text-field>
        <div>
          <div style="display: flex; width: 100%; gap: 15px; justify-content: stretch">
            <v-btn
              v-if="!student.deleted"
              @click="setDelete(true)"
              :loading="state.loadingDelete"
              style="text-transform: capitalize; flex-grow: 1"
              color="#E5E4DB"
            >
              Delete
            </v-btn>
            <v-btn
              v-if="!student.deleted"
              type="submit"
              :loading="state.loadingSave"
              style="text-transform: capitalize; flex-grow: 1"
              >Save</v-btn
            >
            <v-btn
              v-else
              @click="setDelete(false)"
              :loading="state.loadingDelete"
              style="text-transform: capitalize; flex-grow: 1"
              >Restore</v-btn
            >
          </div>
        </div>
      </form>
    </div>
  </SideModal>
</template>

<script setup lang="ts">
import type { StudentsType } from "@/api_types/school";
import SideModal from "@/components/sideModal.vue";
import Typography from "@/components/typography.vue";
import { watch } from "vue";
import { reactive } from "vue";
import { useAlertsStore } from "@/stores/alerts";
import useStudents from "@/stores/school/students";
import { copyToClipboard } from "@/utils";

const props = defineProps<{
  student: StudentsType[number];
  show: boolean;
}>();

const state = reactive({
  loadingSave: false,
  loadingDelete: false,
  child_fname: "",
  child_lname: "",
  description: ""
});

const studentsS = useStudents();

watch([() => props.student, () => props.show], () => {
  if (props.student)
    Object.assign(state, {
      child_fname: props.student.child_fname,
      child_lname: props.student.child_lname,
      description: props.student.description
    });
});

defineEmits<{
  (e: 'update:show', ...args: any[]): void
}>();

const save = async () => {
  // save student
  state.loadingSave = true;
  const res = await studentsS.updateStudent(props.student.uid, {
    child_fname: state.child_fname,
    child_lname: state.child_lname,
    description: state.description
  });

  useAlertsStore().addAlert(
    res ? "success" : "error",
    res ? "Student Details updated successfully" : "Failed to update student's data",
    7
  );
  state.loadingSave = false;
};
const setDelete = async (val: boolean) => {
  // save student
  state.loadingDelete = true;

  const res = await studentsS.updateStudent(props.student.uid, {
    deleted: val
  });

  const message = val ? "delete" : "restore";
  useAlertsStore().addAlert(
    res ? "success" : "error",
    res ? `Student ${message}d successfully` : `Failed to ${message} student`,
    7
  );
  state.loadingDelete = false;
};
</script>

<style scoped lang="scss">
@use "@/assets/_vars.scss" as *;
@use "@/assets/_breakpoints.scss" as breakpoints;

.modal-container {
  padding: 30px;

  .modal-top {
    display: flex;
    align-items: center;
    justify-content: space-between;
  }

  .modal-body {
    display: flex;
    flex-direction: column;
    width: 100%;
    gap: 30px;
  }
}
</style>
